"use client";

import { Menu, Phone, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { siteConfig } from "@latam-energy/content";
import { AppImage as Image } from "./AppImage";
import { AppLink as Link } from "./AppLink";
import { SearchDialog } from "./SearchDialog";

const nav = [
  ["Nosotros", "/nosotros"],
  ["Servicios", "/servicios"],
  ["Soluciones", "/soluciones"],
  ["Productos", "/productos"],
  ["Industrias", "/industrias"],
  ["Proyectos", "/proyectos"],
  ["Blog", "/blog"],
  ["Contacto", "/contacto"]
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        setSearchOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <a className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-white focus:p-3" href="#contenido">Saltar al contenido</a>
      <div className="hidden border-b border-white/10 bg-navy text-sm text-white md:block">
        <div className="container flex min-h-10 items-center justify-between gap-4 text-white/85">
          <span>{siteConfig.email}</span>
          <span>{siteConfig.telephone}</span>
          <span>{siteConfig.address}</span>
          <span>{siteConfig.businessHours}</span>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="container flex min-h-20 items-center justify-between gap-4">
          <Link href="/" aria-label="Latam Energy Services inicio">
            <Image src="/assets/industrial/logo.png" alt="Latam Energy Services" width={320} height={72} priority className="h-14 w-auto object-contain" />
          </Link>
          <nav className="hidden items-center gap-1 rounded-md bg-slate-50 p-1 text-sm font-extrabold text-slate-700 lg:flex" aria-label="Principal">
            {nav.map(([label, href]) => {
              const active = isActiveRoute(location.pathname, href);
              return (
                <Link className={active ? "nav-link nav-link--active" : "nav-link"} key={href} href={href} aria-current={active ? "page" : undefined}>
                  {label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <button className="btn btn-outline px-3" type="button" aria-label="Abrir búsqueda" onClick={() => setSearchOpen(true)}><Search size={19} /></button>
            <Link className="btn btn-primary hidden sm:inline-flex" href="/cotizar"><Phone size={18} aria-hidden="true" /> Solicitar cotización</Link>
            <button className="btn btn-outline px-3 lg:hidden" aria-label="Abrir menú" aria-controls="menu-movil" aria-expanded={open} onClick={() => setOpen(true)}><Menu size={22} /></button>
          </div>
        </div>
        {open ? (
          <div id="menu-movil" className="mobile-menu fixed inset-0 z-50 bg-[#0f2e4d] text-white lg:hidden" role="dialog" aria-modal="true" aria-label="Menú móvil">
            <div className="mobile-menu-panel flex min-h-dvh w-full flex-col bg-[#0f2e4d] px-5 pb-6 pt-[max(18px,env(safe-area-inset-top))] shadow-2xl">
              <div className="flex items-center justify-between gap-4 rounded-md bg-white p-3 shadow-lg">
                <Image src="/assets/industrial/logo.png" alt="Latam Energy Services" width={320} height={72} className="h-14 w-auto object-contain" />
                <button className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-navy text-white" aria-label="Cerrar menú" onClick={() => setOpen(false)}><X size={24} /></button>
              </div>
              <nav className="mt-6 grid gap-3 text-lg font-extrabold">
                {nav.map(([label, href]) => {
                  const active = isActiveRoute(location.pathname, href);
                  return (
                    <Link className={active ? "mobile-nav-link mobile-nav-link--active" : "mobile-nav-link"} key={href} href={href} aria-current={active ? "page" : undefined}>
                      {label}
                    </Link>
                  );
                })}
              </nav>
              <div className="mt-auto grid gap-3 border-t border-white/20 pt-5 text-sm text-white/85">
                <span>{siteConfig.email}</span>
                <span>{siteConfig.telephone}</span>
                <Link className="btn btn-primary mt-2" href="/cotizar">Solicitar cotización</Link>
              </div>
            </div>
          </div>
        ) : null}
      </header>
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

function isActiveRoute(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}
